import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const PWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Already installed as standalone app — never show
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone
    ) return;

    // Permanently dismissed by user
    if (localStorage.getItem('pwa-install-never')) return;

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const mobile = iOS || /Android|Mobi/i.test(navigator.userAgent);

    // Only show on mobile
    if (!mobile) return;

    setIsIOS(iOS);

    if (iOS) {
      // iOS can't trigger native prompt — show card once per session
      if (!sessionStorage.getItem('pwa-ios-shown')) {
        setTimeout(() => setShow(true), 3000);
        sessionStorage.setItem('pwa-ios-shown', 'true');
      }
    } else {
      // Android/Chrome: wait for native beforeinstallprompt
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        if (!sessionStorage.getItem('pwa-card-shown')) {
          setTimeout(() => setShow(true), 3000);
          sessionStorage.setItem('pwa-card-shown', 'true');
        }
      };

      const handleAppInstalled = () => {
        setShow(false);
        setDeferredPrompt(null);
        toast.success('App installed! Find it on your home screen.', { duration: 4000 });
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Native Android/Chrome install
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setShow(false);
      if (outcome !== 'accepted') {
        toast('You can install later from your browser menu ⋮', { icon: 'ℹ️', duration: 4000 });
      }
    }
    // For iOS the button just shows the instructions inline — no dismiss needed
  };

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleNeverShow = () => {
    setShow(false);
    localStorage.setItem('pwa-install-never', 'true');
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 animate-slideUp">
      <div className="rounded-2xl shadow-2xl border overflow-hidden"
        style={{ background: 'white', borderColor: '#e8cc88' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #f9efd5, #e8c47a)' }}>
              <span className="text-xl">🩸</span>
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: '#3e2b11' }}>Install OCYM Blood App</p>
              <p className="text-xs" style={{ color: '#8B6914' }}>Quick access · Works offline</p>
            </div>
          </div>
          <button onClick={handleDismiss} className="p-1 rounded-lg text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* iOS instructions or Android install button */}
        <div className="px-4 pb-4">
          {isIOS ? (
            <div className="rounded-xl p-3 mb-3 text-sm" style={{ background: '#fdf8ee' }}>
              <p className="font-semibold mb-2" style={{ color: '#5c3a1e' }}>To install on iPhone/iPad:</p>
              <ol className="space-y-1 text-xs" style={{ color: '#6b4820' }}>
                <li>1. Tap the <strong>Share</strong> button (□↑) in Safari</li>
                <li>2. Scroll and tap <strong>"Add to Home Screen"</strong></li>
                <li>3. Tap <strong>"Add"</strong></li>
              </ol>
            </div>
          ) : (
            <button
              onClick={handleInstall}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white mb-3 transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #c4451a, #8f2a0e)', boxShadow: '0 4px 14px rgba(196,69,26,0.4)' }}
            >
              📲 Install App
            </button>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleDismiss}
              className="flex-1 py-1.5 rounded-lg text-xs border font-medium"
              style={{ borderColor: '#e8cc88', color: '#6b4820' }}
            >
              Later
            </button>
            <button
              onClick={handleNeverShow}
              className="flex-1 py-1.5 rounded-lg text-xs font-medium"
              style={{ color: '#a87538' }}
            >
              Don't ask again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstall;
